import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DrawingService, LayerStylesService } from '@ebw-monorepo/map';
import { EbwModalService } from '@ebw-monorepo/modal';
import { SplitViewService, UtilsService } from '@ebw-monorepo/utils';
import { TranslateService } from '@ngx-translate/core';
import { SplitViewConf } from 'libs/ng-components/utils/src/lib/models/split-view-conf';
import moment from 'moment';
import { GeoJSON } from 'ol/format';
import { Circle } from 'ol/geom';
import { fromCircle, fromExtent } from 'ol/geom/Polygon';
import { debounceTime, filter, map, Observable, startWith, Subscription, switchMap, tap } from 'rxjs';

import { LoginService } from '@ebw-monorepo/login';
import { Feature } from 'ol';
import { IMapTable } from '../../../interfaces/mapTable.interface';
import { API_URLS, ApiUrlService } from '../../../../../../utils/src/lib/services/api-url.service';
import { MapTableService } from '../../../services/map-table.service';
import { MapService } from '../../../services/map.service';
import { MapSearchService } from '../../controls/map-search/map-search.service';
import { CheckinOutTableComponent } from './checkin-out-table/checkin-out-table.component';

export enum AttivitaStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  EXPIRED = 'expired'
}

export enum AttivitaType {
  AREA = 'area',
  POINT = 'point',
  ALL = '-1'
}

export class UserFilter {
  id: string;
  username: string;
}

@Component({
  selector: 'ebw-monorepo-checkin-out',
  templateUrl: './checkin-out.component.html',
  styleUrls: ['./checkin-out.component.scss']
})
export class CheckInOutComponent implements OnInit, OnDestroy {
  public filtersForm: FormGroup;
  public optical_segment_checked: Boolean;
  public statusEnum = AttivitaStatus;
  public typeEnum = AttivitaType;
  public userFilterOptions: UserFilter[] = [];
  public userfilteredOptions$: Observable<UserFilter[]>;
  public autoCompletefilteredOptions;
  public customAreaSearchEnable = false;
  public customAreaSearchSet = false;
  private _contextId = 'CheckInTable';
  private _drawingGeometrySub: Subscription;
  // flag to check whether the program should listen for changes on the form
  private _listening = true;

  companiesList: any = [];
  formCompany: any;
  selectedCompany: null;

  // ------------------------------------------------------------------------------------------------

  constructor(
    private _httpClient: HttpClient,
    private _splitViewService: SplitViewService,
    private _mapTableService: MapTableService,
    private _ebwModal: EbwModalService,
    private _translateService: TranslateService,
    private _mapSearchService: MapSearchService,
    private _mapService: MapService,
    private _drawingService: DrawingService,
    private _layerStyleService: LayerStylesService,
    private _apiUrlService: ApiUrlService,
    private _utilsService: UtilsService
  ) {}

  // ------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this._getFilterUserOptions();

    let url = this._apiUrlService.createUrl(API_URLS.USER.COMPANY.LIST);
    this._utilsService.getRequest(url, {}).subscribe({
      next: (data) => {
        this.companiesList = data;
        if (data.length == 1) {
          this.formCompany = data[0];
        }
      },
      error: (err) => {
        console.error('[checkin-out] - [getCompanies] - error', err);
        this._ebwModal.openErrorModal({
          title: this._translateService.instant('common.error'),
          content: 'Errore recupero aziende'
        });
      }
    });

    this.filtersForm = new FormGroup({
      type: new FormControl('-1'),
      user: new FormControl(null),
      status: new FormControl(-1),
      date_range: new FormGroup({
        start: new FormControl(null),
        end: new FormControl(null)
      }),
      fw_link_id: new FormControl(null),
      search_buffer: new FormControl(null),
      geom: new FormControl(null),
      item_req: new FormControl(null)
    });

    this.optical_segment_checked = false;

    this.userfilteredOptions$ = this.filtersForm.get('user').valueChanges.pipe(
      map((value) => (typeof value === 'string' ? value : value?.username)),
      map((name) => (name ? this._userfilter(name) : this.userFilterOptions.slice()))
    );

    // listen to changes in the form field 'fw_link_id'
    this.filtersForm
      .get('fw_link_id')
      .valueChanges.pipe(
        filter((value: string) => this._listening), // don't listen unless the flag is on
        startWith(''),
        tap((value: string) => {
          // set the buffer as not required in case the form is empty
          if (value === '') {
            this.filtersForm.get('search_buffer').clearValidators();
            this.filtersForm.get('search_buffer').updateValueAndValidity();
          }
        }),
        filter((value: string) => value?.length >= 3), // don't listen past this until the input is longer than 3 characters
        debounceTime(300), // wait 300ms before continuing, ignore previous events that happened within that time
        switchMap((val) => {
          // set the buffer as required
          this.filtersForm.get('search_buffer').setValidators(Validators.required);
          this.filtersForm.get('search_buffer').updateValueAndValidity();

          // make a SOLR search with the fw_link filter
          return this._mapSearchService.getAutocompleteOpts(val, 'url:(*fw_link*)');
        })
      )
      .subscribe((val) => {
        // store the result of the SOLR search as autocomplete options
        this.autoCompletefilteredOptions = val;
      });

    this._drawingToolSubscription();
  }

  ngOnDestroy(): void {
    // senza questa, ogni geometria disegnata altrove (DXF, query, toolbar) verrebbe gestita anche da questa istanza distrutta
    this._drawingGeometrySub?.unsubscribe();
  }

  onSelectCompany(event: any) {
    this.formCompany = this.companiesList.find((el) => el.id == event.value);
    this._getFilterUserOptions(event.value);
  }

  changeChecked() {
    this.optical_segment_checked = !this.optical_segment_checked;
  }

  displayFnUserAutoComplete(user: UserFilter): string {
    return user && user.username ? user.username : '';
  }

  onUserSelected(event: any) {
    const user = event.option.value;
    const company = this.companiesList.find((el) => el.name == user.system);

    if (company) {
      this.formCompany = company;
      this.selectedCompany = company.id;
    } else {
      this.formCompany = null;
      this.selectedCompany = null;
    }
  }

  search() {
    let config: IMapTable = {
      url: undefined,
      title: 'map.sidebar.checkInOut.title',
      filters: this._mappedFilter()
    };

    const splitViewConf: SplitViewConf = {
      title: config.title,
      icon: 'table_view'
    };
    this._splitViewService.show(CheckinOutTableComponent, this._contextId, null, splitViewConf);
    this._mapTableService.setMapTableConfig(config);
  }

  /**
   * method triggered when an autocomplete option is selected
   * @param event
   */
  onAutocompleteSelectionChanged(event: any) {
    // prevent the form observer from listening
    this._listening = false;
    // set the value of the fw_link_id to the ID of the selected option
    this.filtersForm.get('fw_link_id').setValue(event.option.value.urns.split('keysZ')[1]);
    // allow the form observer to listen
    this._listening = true;
    // reset the list of autocomplete options
    this.autoCompletefilteredOptions = null;
  }

  public onCustomAreaSearchEnable() {
    this.filtersForm.get('geom').reset();
    this.filtersForm.get('fw_link_id').reset();
    this.filtersForm.get('search_buffer').reset();
    this.customAreaSearchSet = false;
    this._removeGeoFromMap();
  }

  public openDrawingTool() {
    this._drawingService.openDrawingTool({
      point: false,
      line: false,
      polygon: true,
      circle: true,
      text: false
    });
  }

  public setFullArea() {
    const map = this._mapService.openLayersMap;
    const newExtent = this._mapService.openLayersMap.getView().calculateExtent(map.getSize());
    const newPolygon = fromExtent(newExtent);

    var feature = new Feature({
      geometry: newPolygon
    });

    this._setArea(feature);

    var currZoom = map.getView().getZoom();

    map.getView().animate({
      zoom: currZoom - 0.2,
      duration: 250
    });
  }

  public clearArea() {
    this.filtersForm.get('geom').reset();
    this._removeGeoFromMap();
    this.customAreaSearchSet = false;
  }

  /** Funzione per la gestione della geometria  */
  private _drawingToolSubscription() {
    this._drawingGeometrySub = this._drawingService.drawingGeometry$.subscribe({
      next: (feature) => {
        this._setArea(feature);
        this._drawingService.closeDrawingTool();
      },
      error: (error) => {
        console.log(error);
      }
    });
  }

  private _addGeomMapLayer(geometry) {
    const geoFeatureStyle = this._layerStyleService.buildFeatureStyleFromId('BlueFill');

    this._mapService.centerMapOnGeometry(geometry, false);
    this._mapService.addFeatureToTemporaryLayer(geometry, geoFeatureStyle, true, false, 'update_query_geom');
  }

  private _removeGeoFromMap() {
    this._mapService.clearFeatureFromTemporaryLayerById('update_query_geom', true);
  }

  private _mappedFilter() {
    let value = this.filtersForm.getRawValue();
    let filter: any = {
      type: value.type !== '-1' ? value.type : null,
      user_id: typeof value.user !== 'string' ? value.user?.user_id : undefined,
      status: value.status !== -1 ? value.status : null,
      dt_start: value.date_range
        ? value.date_range?.start
          ? moment(value.date_range?.start).format('YYYY-MM-DD')
          : null
        : null,
      dt_end: value.date_range
        ? value.date_range?.end
          ? moment(value.date_range?.end).format('YYYY-MM-DD')
          : null
        : null,
      fw_link_id: value.fw_link_id,
      search_buffer: value.search_buffer,
      geom: value.geom,
      item_req: value.item_req || null
    };

    if (this.formCompany) {
      filter.system = this.formCompany.name;
    }

    return filter;
  }

  private _userfilter(name: string): UserFilter[] {
    const filterValue = name?.toLowerCase();

    return this.userFilterOptions.filter(
      (option) => option.username?.toLowerCase().includes(filterValue) || option.id?.toLowerCase().includes(filterValue)
    );
  }

  private _getFilterUserOptions(companyId?: number) {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY)
    });

    let url = `${this._apiUrlService.createUrl(API_URLS.CHECKIN.LIST_USERS)}${
      companyId ? '?company_id=' + companyId : ''
    }`;
    //make the http request to execute the query and retrieve the records
    this._httpClient.get(url, { headers }).subscribe({
      next: (resp: { numRows: number; mapped: UserFilter[]; error: any; message: string }) => {
        if (resp.mapped.length > 0) {
          this.userFilterOptions = resp.mapped;
          this.filtersForm.get('user').enable();
        } else {
          this.filtersForm.get('user').disable();
        }
      },
      error: (err) => {
        this._ebwModal.openErrorModal({
          title: this._translateService.instant('common.error'),
          content: err.message
        });
      }
    });
  }

  private _setArea(feature) {
    this.customAreaSearchSet = true;
    this._removeGeoFromMap();

    let format = new GeoJSON({
      dataProjection: MapService.EPSG_3857_KEY,
      featureProjection: MapService.EPSG_3857_KEY
    });

    let featureGeometry;

    if (feature.getGeometry().getType() === 'Circle') {
      featureGeometry = fromCircle(<Circle>feature.getGeometry());
    } else {
      featureGeometry = feature.getGeometry();
    }

    let geometry = format.writeGeometryObject(featureGeometry);

    let geometrySrt4326 = format.writeGeometry(featureGeometry, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857'
    });

    geometry['crs'] = {
      type: 'name',
      properties: {
        name: 'EPSG:3857'
      }
    };

    this._addGeomMapLayer(geometry);
    this.filtersForm.get('geom').setValue(geometrySrt4326);
  }

  reset() {
    this.filtersForm.reset();
    this.selectedCompany = null;
    this.formCompany = null;
    this._getFilterUserOptions();
  }
}
